import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getCookie } from 'common/utils/cookies';

// Create a base query with runtime configuration
export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  if (!window.APP_CONFIG) {
    throw new Error(
      'APP_CONFIG is not loaded yet. Make sure configuration is loaded before using API.',
    );
  }

  const dynamicBaseQuery = fetchBaseQuery({
    baseUrl: `${window.APP_CONFIG.env.API_HOST}`,
    timeout: 30000,
    prepareHeaders: headers => {
      // Set common headers
      headers.set('Access-Control-Allow-Origin', '*');
      headers.set(
        'Access-Control-Allow-Methods',
        'GET,POST,OPTIONS,DELETE,PUT',
      );

      // Add authorization token
      const accessToken = getCookie('access_token');
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      return headers;
    },
  });

  return dynamicBaseQuery(args, api, extraOptions);
};

// Enhanced base query with error handling (optional)
export const baseQueryWithRetry: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // Handle token refresh or other global error handling here if needed
  if (result.error && result.error.status === 401) {
    // Handle unauthorized access - redirect to login or refresh token
    console.log('Unauthorized access detected');
  }

  return result;
};
